import * as React from 'react'

import { Tooltip, colors, useToggle } from '@factorialco/deprecated-design-system'
import classNames from 'classnames/bind'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'

import type CalendarEvent from 'modules/calendar/store/models/CalendarEvent'
import { HALF_DAY_TYPES } from 'modules/timeoff/store/models/Leave'

import type LeaveStore from '../LeaveStore'
import styles from './styles/index.module.scss'

const cx = classNames.bind(styles)

function cleanColor(color: string) {
  return color.replace('#', '')
}

const HOLIDAY = 'holiday'
const HOVERED = 'hovered'

/* eslint-enable quote-props */
interface Props {
  calendarEvent: CalendarEvent
  colored: boolean
  visible: boolean
  leaveStore: LeaveStore
  title: string
}

const EventRow = (props: Props) => {
  const COLOR_CODES = {
    FF9153: 'redClass',
    FF869E: 'darkPinkClass',
    FBE3BC: 'lightYellowClass',
    '6AC7CE': 'lightGreenClass',
    '68CC45': 'darkGreenClass',
    '07A2AD': 'turquoiseBlueClass',
    '47A7FF': 'lightBlueClass',
    '877FED': 'darkPurpleClass',
    '515164': 'darkGreyClass',
    AEAEB8: 'lightGreyClass',
    E2E2E5: 'other',
    F5BA58: 'sick',
    [cleanColor(colors.viridian1000)]: HOLIDAY,
    [cleanColor(colors.grey600)]: 'compassionate',
    [cleanColor(colors.grey400)]: HOVERED,
  }

  const { calendarEvent, colored, visible, leaveStore, title } = props
  const { end_of_day: endOfDay } = HALF_DAY_TYPES
  const { half_day: halfDay, approved } = calendarEvent.attributes.metadata
  const [open, toggle] = useToggle(false)
  const generic = i18n.t('settings.leave_types.defaults.generic')

  const typeName = colored || visible ? calendarEvent.metadata('leave_type_name') : generic

  const leaveName = `${typeName} - ${title}`

  const tooltipContent: string = !approved
    ? i18n.t('calendar.tooltip_pending_leave', {
        leaveName,
      })
    : leaveName

  const hovering = (): boolean => {
    const { leave_id: leaveId } = calendarEvent.attributes.metadata
    const hoveredLeaveId = leaveStore.hoveredLeaveId

    return !!hoveredLeaveId && hoveredLeaveId !== leaveId
  }

  const rowClass = (): string => {
    if (hovering()) return HOVERED

    if (!colored && !visible) return HOLIDAY

    return COLOR_CODES[calendarEvent.metadata('leave_color')] || HOLIDAY
  }

  return (
    <div
      className={cx('event', {
        event_Anonymous: !colored,
        event_Hovering: () => hovering(),
      })}
    >
      <Tooltip.Alt
        attachment="bottom left"
        targetAttachment="top left"
        open={open}
        toggle={toggle}
        offset="0 -10px"
        label={tooltipContent}
        textAlign="left"
      >
        {calendarEvent.attributes.metadata.start_on !==
        calendarEvent.attributes.metadata.finish_on ? (
          <div
            className={cx('content', rowClass(), {
              halfDay: false,
              secondHalf: false,
              notApproved: !approved,
            })}
          >
            <div className={styles.text}>{title}</div>
          </div>
        ) : (
          <div
            className={cx('content', rowClass(), {
              halfDay: !!halfDay,
              secondHalf: halfDay === endOfDay,
              notApproved: !approved,
            })}
          >
            <div className={styles.text}>{title}</div>
          </div>
        )}
      </Tooltip.Alt>
    </div>
  )
}

export default observer(EventRow)
